interface GenreFilm {
  id: string;
  title: string;
  year: number;
  region: string;
  rating: string;
  summary: string;
  meta: string;
  poster: string;
}

interface GenreFeatureSectionProps {
  id: string;
  eyebrow: string;
  title: string;
  desc: string;
  tone: "primary" | "accent" | "secondary";
  films: GenreFilm[];
  imageSide: "left" | "right";
  bgClass: string;
}

const toneBadge: Record<string, string> = {
  primary: "bg-primary-100 text-primary-800",
  accent: "bg-accent-100 text-accent-800",
  secondary: "bg-secondary-100 text-secondary-800",
};

const toneText: Record<string, string> = {
  primary: "text-primary-700",
  accent: "text-accent-700",
  secondary: "text-secondary-700",
};

export default function GenreFeatureSection({
  id,
  eyebrow,
  title,
  desc,
  tone,
  films,
  imageSide,
  bgClass,
}: GenreFeatureSectionProps) {
  const lead = films[0];
  const rest = films.slice(1);
  const imageFirst = imageSide === "left";

  return (
    <section id={id} className={`w-full py-14 md:py-20 ${bgClass}`}>
      <div className="w-full px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div
            className={`relative ${imageFirst ? "lg:order-1" : "lg:order-2"}`}
          >
            <div className="w-full aspect-[4/5] max-w-md mx-auto lg:max-w-none rounded-lg overflow-hidden border border-background-200/80 bg-background-200">
              <img
                src={lead.poster}
                alt={`${lead.title} 海报`}
                title={`${lead.title} ${eyebrow}片单`}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="hidden sm:flex absolute -bottom-5 -right-3 lg:-right-5 items-center gap-3 rounded-lg bg-background-50 border border-background-200/80 px-4 py-3 max-w-[15rem]">
              <span className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-700 shrink-0">
                <i className="ri-star-smile-line text-xl" />
              </span>
              <div className="min-w-0">
                <p className="text-xs text-foreground-500 font-label">代表片目</p>
                <p className="text-sm font-label font-semibold text-foreground-950 truncate">
                  {lead.title}
                </p>
              </div>
            </div>
          </div>

          <div className={imageFirst ? "lg:order-2" : "lg:order-1"}>
            <span
              className={`inline-flex items-center gap-2 h-7 px-3 rounded-full text-xs font-label font-medium ${toneBadge[tone]}`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-current" />
              {eyebrow}
            </span>
            <h2 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-950">
              {title}
            </h2>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
              {desc}
            </p>

            <div className="mt-6 rounded-lg border border-background-200/80 bg-background-50/70 p-4">
              <p className="text-sm font-label font-semibold text-foreground-950">
                {lead.title}
                <span className="ml-2 text-xs font-normal text-foreground-500">
                  {lead.year} · {lead.region}
                </span>
              </p>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                {lead.summary}
              </p>
            </div>

            <ul className="mt-5 divide-y divide-background-200/80">
              {rest.map((film) => (
                <li
                  key={film.id}
                  className="flex items-start justify-between gap-4 py-3"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-label font-medium text-foreground-900">
                      {film.title}
                      <span className="ml-2 text-xs font-normal text-foreground-400">
                        {film.year}
                      </span>
                    </p>
                    <p className="mt-0.5 text-xs text-foreground-500 font-label">
                      {film.region} · {film.meta}
                    </p>
                  </div>
                  <span className={`text-sm font-label font-semibold shrink-0 ${toneText[tone]}`}>
                    {film.rating}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}